import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Form, Button, Alert, Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";

import { useAuth } from "@/context/AuthContext";
import { Pages } from "@/shared/Constants";
import { UserProfile } from "@/shared/models/UserProfile";
import { UserProfileApi } from "@/services/UserProfile.api";
import { RootState, store } from "@/redux/store";
import { useCommonData } from "@/hooks/useCommonData";
import { GetTerminalDisplayName } from "@/shared/models/Terminal";
import CardContainer from "@/components/CardContainer";
import FormGroupMultiSelectOutside from "@/components/FormGroupMultiSelectOutside";
import { setInitializationData } from "@/redux/dashboardSlice";
import { setIsClientProfileSet } from "@/redux/authSlice";
import { isEditor, isHandler } from "@/shared/utils/roles";

const ClientProfilePage: React.FC = () => {
  const { userName } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [apexId, setApexId] = useState("");
  const [userProfileId, setUserProfileId] = useState<number | undefined>(undefined);

  const { terminals, products, regionOptions, loadCommonData } = useCommonData();
  const { apexId: currentApexId, userRoles: authUserRoles } = useSelector((state: RootState) => state.auth);

  // Form data - arrays of primaryTermicalIds and productClasses
  const [formData, setFormData] = useState({
    selectedTerminalIds: [] as string[], // Contains primaryTermicalId values
    selectedProductIds: [] as string[], // Contains productClass values
    subscribeToFaultDigestDaily: false,
    subscribeToFaultDigestWeekly: false,
    selectedRegions: [] as string[],
  });

  const [selectedUser, setSelectedUser] = useState<any>(null);

  // Extract apexId from URL parameters or use current user's apexId
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    const apexIdParam = searchParams.get("apexId");
    // Pull user object from navigation state if provided
    const navUser: any = (location.state as any)?.user;
    if (navUser) {
      // Only update if changed to avoid re-renders
      setSelectedUser((prev: any) => (prev?.apexId === navUser.apexId ? prev : navUser));
    }
    const targetApexId = apexIdParam || navUser?.apexId || currentApexId;

    // Prevent redundant state updates to avoid loops
    setApexId((prev) => (prev === targetApexId || !targetApexId ? prev : targetApexId));

    initializedRef.current = true;
    // Exclude location.state to avoid identity changes causing loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentApexId]);

  // Load terminals, products, and existing profile data
  // Prevent redundant profile loading for the same apexId
  const lastLoadedApexIdRef = useRef<string | null>(null);
  useEffect(() => {
    const loadData = async () => {
      // Load dropdown data
      await loadCommonData();

      // Load existing profile if user has one and we have a target apexId
      if (apexId && lastLoadedApexIdRef.current !== apexId) {
        const existingProfile = await UserProfileApi.getUserProfile(apexId);

        if (existingProfile && existingProfile.id) {
          // Store UserProfile ID and metadata separately
          setUserProfileId(existingProfile.id);

          setFormData({
            selectedTerminalIds: existingProfile.primaryTerminalIds || [],
            selectedProductIds: existingProfile.productClassIds || [],
            subscribeToFaultDigestDaily: existingProfile.subscribeToFaultDigestDaily || false,
            subscribeToFaultDigestWeekly: existingProfile.subscribeToFaultDigestWeekly || false,
            selectedRegions: existingProfile.region || [],
          });
          // If arriving from header (no nav user), populate selectedUser from auth + profile
          setSelectedUser(
            (prev: any) =>
              prev ?? {
                apexId,
                domain: (existingProfile as any).domain || undefined,
                userRole: authUserRoles || [],
              }
          );
          lastLoadedApexIdRef.current = apexId;
        } else {
          console.log("📝 No existing profile found, starting with empty form");
          setUserProfileId(undefined);
          lastLoadedApexIdRef.current = apexId;
        }
      }
    };

    loadData();
    // Removed loadCommonData from dependencies as it is likely being recreated on every render, causing the effect to run repeatedly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apexId, authUserRoles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Prepare UserProfile object for API
      const userProfile: UserProfile = {
        apexId: apexId,
        productClassIds: formData.selectedProductIds, // Array of productClass values
        primaryTerminalIds: formData.selectedTerminalIds, // Array of primaryTermicalId values
        subscribeToFaultDigestDaily: formData.subscribeToFaultDigestDaily,
        subscribeToFaultDigestWeekly: formData.subscribeToFaultDigestWeekly,
        // Save only the region array (no single code)
        region: formData.selectedRegions.length ? formData.selectedRegions : undefined,
      };

      // Use UserProfile ID if updating existing profile
      const isUpdating = userProfileId !== undefined;
      if (isUpdating) {
        userProfile.id = userProfileId;
      }

      // Call API to save user profile
      const success = isUpdating
        ? await UserProfileApi.saveUserProfile(userProfile)
        : await UserProfileApi.addUserProfile(userProfile);

      if (success) {
        store.dispatch(
          setInitializationData({
            isInitialized: true,
            filterRegionList: userProfile.region || [],
            filterTerminalList: userProfile.primaryTerminalIds || [],
            filterProductList: userProfile.productClassIds || [],
          })
        );

        store.dispatch(setIsClientProfileSet(true));
        handleSkip();
      }
    } catch (error) {
      console.error("Error saving client profile:", error);
    }
  };

  const handleSkip = () => {
    if (location.state?.from === Pages.userManagement) {
      navigate(-1);
    } else {
      navigate(`/${Pages.dashboard}`, { replace: true });
    }
  };

  const isClientProfileSet = userProfileId !== undefined;

  return (
    <CardContainer
      title={isClientProfileSet ? "Edit Client Profile" : "Complete Your Client Profile"}
      subTitle="Configure terminals, products, and subscription preferences"
    >
      <div className="p-4">
        <Alert variant={isClientProfileSet ? "warning" : "info"}>
          <strong>Welcome{userName ? `, ${userName}` : ""}!</strong>
          <br />
          {apexId ? (
            isClientProfileSet ? (
              <>
                You are updating this user's client profile. Adjust terminals, products, and countries below, then choose either a
                Daily or Weekly fault digest subscription. When satisfied, save your changes.
              </>
            ) : (
              <>
                Let's set up your client profile. Select the terminals, products, and countries relevant to you, then pick either
                a Daily or Weekly fault digest subscription (only one can be active). When finished, save to continue.
              </>
            )
          ) : (
            "Please complete your client profile to personalize your experience with the Telemetry Fault Log system."
          )}
        </Alert>

        {/* User summary row */}
        {selectedUser && (
          <div className="d-flex flex-wrap align-items-center mb-3 bg-light p-3 rounded border">
            <div className="me-4">
              <strong>Apex ID:</strong> <span className="text-primary">{selectedUser.apexId}</span>
            </div>
            {selectedUser.domain && (
              <div className="me-4">
                <strong>Domain:</strong> {selectedUser.domain}
              </div>
            )}
            {Array.isArray(selectedUser.userRole) && selectedUser.userRole.length > 0 && (
              <div className="me-4">
                <strong>Roles:</strong> {selectedUser.userRole.join(", ")}
              </div>
            )}
          </div>
        )}
        <Form onSubmit={handleSubmit}>
          <Row>
            {!isHandler(authUserRoles) && (
              <Col>
                <FormGroupMultiSelectOutside
                  label="Region"
                  placeHolder="Add a Region..."
                  selectChanged={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      selectedRegions: e,
                    }))
                  }
                  defaultSelectedOptions={formData.selectedRegions}
                  options={regionOptions}
                ></FormGroupMultiSelectOutside>
              </Col>
            )}

            {!isEditor(authUserRoles) && (
              <>
                <Col xs={4}>
                  <FormGroupMultiSelectOutside
                    label="Terminal"
                    placeHolder="Add a Terminal..."
                    selectChanged={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        selectedTerminalIds: e,
                      }))
                    }
                    defaultSelectedOptions={formData.selectedTerminalIds}
                    options={terminals.map((terminal) => ({
                      value: terminal.primaryTermicalId!.toString(),
                      label: `${GetTerminalDisplayName(terminal)}`,
                    }))}
                  ></FormGroupMultiSelectOutside>
                </Col>

                <Col>
                  <FormGroupMultiSelectOutside
                    label="Products"
                    placeHolder="Add a Products..."
                    selectChanged={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        selectedProductIds: e,
                      }))
                    }
                    defaultSelectedOptions={formData.selectedProductIds}
                    options={products.map((prod) => ({ value: prod.productClass!.toString(), label: prod.displayProductName! }))}
                  ></FormGroupMultiSelectOutside>
                </Col>
              </>
            )}
            <Col>
              <Form.Group className="w-100" style={{ minWidth: "300px" }}>
                <Form.Label>Subscription to Fault Digest</Form.Label>
                <div className="d-flex gap-3 mt-2">
                  <Form.Check
                    type="checkbox"
                    id="subscribeDaily"
                    label="Daily"
                    checked={formData.subscribeToFaultDigestDaily}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        subscribeToFaultDigestDaily: e.target.checked,
                        subscribeToFaultDigestWeekly: e.target.checked ? false : prev.subscribeToFaultDigestWeekly,
                      }))
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    id="subscribeWeekly"
                    label="Weekly"
                    checked={formData.subscribeToFaultDigestWeekly}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        subscribeToFaultDigestWeekly: e.target.checked,
                        subscribeToFaultDigestDaily: e.target.checked ? false : prev.subscribeToFaultDigestDaily,
                      }))
                    }
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          <hr />

          <div className="d-flex justify-content-between">
            {isClientProfileSet ? (
              <Button variant="secondary" onClick={handleSkip}>
                Close
              </Button>
            ) : (
              <div />
            )}
            <Button variant="primary" type="submit">
              {isClientProfileSet ? "Update Profile" : "Save Profile & Continue"}
            </Button>
          </div>
        </Form>
      </div>
    </CardContainer>
  );
};

export default ClientProfilePage;
